import React, { useMemo, useState } from "react"
import Button from "../ui-kit/dfb_button"
import CCardAd from "../elements/c_card_ad"
import DfbHeadline from "../ui-kit/dfb_headline"
import MSuperBanner from "../blocks/m_superbanner"
import Section from "../blocks/section"
import CFilter from "../elements/c_filter"
import CPartnerSlider from "../elements/c_partner_slider"
import CCardNews from "../elements/c_card_news"
import MImagecarouselSpecial from "../blocks/m_imagecarousel_special"
import { formatDate } from "../utilities"
import { useTranslations } from "../../hooks/useTranslations"
import { useLocation } from "@reach/router"

const BATCH_SIZE = 6

function showBannerForBatchIndex(index) {
  // hide the banner every 3rd batch (skipping it the first time on the second batch with index 1)
  return !(index % 3) || !((index + 1) % 3)
}

function partnersExists(partners) {
  if (partners) {
    for (const partner of partners.partners) {
      if (partnerExists(partner)) return true;
    }

    for (const partner of partners.premiumPartners) {
      if (partnerExists(partner)) return true;
    }
  }

  return false;
}

function partnerExists(partner) {
  return partner && ((partner.name && partner.name.length) || (partner.logo !== 'none') || partner.url);
}

function MCardNews({ data }) {
  const { headline, news_categories, collection, contests, partners } = data

  const t = useTranslations()
  const { pathname } = useLocation()

  const [selectedFilters, setSelectedFilters] = useState([])
  const [openedBatchCount, setOpenedBatchCount] = useState(0)

  const isHome = pathname === "/"

  const batches = useMemo(() => {
    setOpenedBatchCount(0)

    let filteredCollection = collection || []

    if (selectedFilters.length > 0) {
      filteredCollection = collection.filter((item) =>
        item.content?.root_categories?.some((cat) =>
          selectedFilters.includes(cat.uid)
        )
      )
    }

    const result = []
    let offset = 0

    if (!contests) {
      const adjustedBatchSize = BATCH_SIZE + 2
      result.push(filteredCollection.slice(0, adjustedBatchSize))
      offset = 2
    } else {
      result.push(filteredCollection.slice(0, BATCH_SIZE))
    }

    for (let i = BATCH_SIZE; i < filteredCollection.length; i += BATCH_SIZE) {
      result.push(filteredCollection.slice(i + offset, i + offset + BATCH_SIZE))
    }

    setTimeout(() => {
      // Check if window exists to avoid SSR errors
      if (
        typeof window !== "undefined" &&
        typeof window.upgradeAllBehaviours === "function"
      )
        window.upgradeAllBehaviours()
    }, 100)

    return result
  }, [collection, selectedFilters])

  const allCategories = useMemo(() => {
    // When specific categories are selected we also want the filters to be exactly like that
    const wantedFilterCategories = news_categories
      ? news_categories.split(",").map((cat) => Number.parseInt(cat, 10))
      : []

    const result = []
    for (let i = 0; i < collection?.length; i += 1) {
      const categories = collection[i].content?.root_categories
      if (categories) {
        for (let j = 0; j < categories.length; j += 1) {
          if (!result.some((cat) => cat.uid === categories[j].uid)) {
            result.push(categories[j])
          }
        }
      }
    }

    if (wantedFilterCategories.length > 0) {
      // Only keep the selected categories in the order they were specified
      return wantedFilterCategories
        .map((catId) => result.find((cat) => cat.uid === catId))
        .filter(Boolean)
    }
    return result
  }, [collection, news_categories])

  const activeContests = {
    overline: contests?.title,
    items: contests?.items
      ?.filter(
        (contest) =>
          !!contest.contest_start_date ||
          (new Date(contest.contest_start_date).getTime() <= Date.now() &&
            !!contest.contest_end_date) ||
          new Date(contest.contest_end_date).getTime() > Date.now()
      )
      ?.map((contest) => ({
        ...contest,
        image: contest.image?.[0],
        disclaimer: contest.contest_end_date
          ? `Laufzeit: ${formatDate(new Date(contest.contest_end_date))}`
          : undefined,
      })),
  }
  const showMore = () => {
    setOpenedBatchCount((count) => count + 1)
  }

  if (batches.length === 0) {
    return null
  }

  const hasPartners = partnersExists(partners)

  return (
    <Section baseClass="m-CardNews" defaultPadding="small" data={data} testid="card-news-section">
      <div className="m-CardNews-wrapper">
        <div className="m-CardNews-container">
          {headline && (
            <DfbHeadline
              headline_level="h2"
              headline={headline}
              add_class="m-CardNews-headline"
              testid="card-news-headline"
            />
          )}
          {isHome && allCategories.length > 1 && (
            <CFilter
              allItemsLabel={t("card_news.all_items")}
              items={allCategories}
              onChange={setSelectedFilters}
            />
          )}
          <div className="m-CardNews-grid" data-testid="cards">
            {batches[0].map((card) => (
              <CCardNews key={`${card.uid}_${card.full_slug}`} data={card} />
            ))}
            {activeContests.items?.length > 0 && (
              <MImagecarouselSpecial
                data={activeContests}
                className="m-CardNews-special"
                defaultButtonText={t("card_news.contest_link")}
              />
            )}
            <div className={`m-CardNews-partner-ad${!hasPartners ? ' without-partners' : ''}`}>
              {hasPartners && <CPartnerSlider data={partners} />}
              <CCardAd />
            </div>
          </div>
          {batches.slice(1).map((batch, index) => (
            <div
              key={`batch-${selectedFilters.join("-")}-${index}`}
              className="m-CardNews-load-more-container m-CardNews-grid"
              style={
                openedBatchCount <= index
                  ? {}
                  : {
                      height: "auto",
                      overflow: "visible",
                      marginTop: "1.25rem",
                    }
              }
            >
              {batch.map((card) => (
                <CCardNews
                  key={`${card.title}_${card.full_slug}`}
                  data={card}
                />
              ))}
            </div>
          ))}
          {openedBatchCount < batches.length - 1 && (
            <div className="m-CardNews-load-more">
              <div className="m-CardNews-superbanner" data-testid="superbanner">
                <MSuperBanner />
              </div>

              <Button
                tagName="button"
                type="button"
                onClick={showMore}
                text={t("dfb_anchor.loadmore")}
                test_id="load-more-button"
              />
            </div>
          )}
        </div>
      </div>
    </Section>
  )
}

export default MCardNews
